import {
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  LinearProgress,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ExcelJobDTO } from '@psw/shared';
import {
  CheckCicle,
  Close,
  Download,
  filterSdxIconToRedDark,
  filterSdxIconToRedLight,
  filterSdxIconToWhite,
  SdxColors,
  Warning,
  Warning1,
} from '@sodexo/ui-components';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarLoader } from 'react-spinners';
import i18n from '../i18n';
import {
  updateExcelJobInCache,
  useAbortExcelGeneration,
  useDownloadExcel,
  useListExcelJobs,
} from '../Queries/excel.queries';
import { FetchService } from '../services/fetch.service';
import { showErrorToast, showSuccessToast } from '../Utils/toast.util';

export function ExcelDownloadProgress() {
  const { t, i18n } = useTranslation('translation', { keyPrefix: 'components.excelDownloadProgress' });

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const excelJobs = useListExcelJobs();
  const downloadExcel = useDownloadExcel();
  const abortExcel = useAbortExcelGeneration({
    onSuccess: () => {
      showSuccessToast(t('abortedToast'));
    },
  });

  const websocketListener = useCallback(event => onWebsocketMessage(event, downloadExcel), []);

  useEffect(() => {
    const listenWebsocket = async () => {
      const listenerId = await FetchService.addWebSocketListener({
        type: 'message',
        listener: websocketListener,
      });

      return () => {
        FetchService.removeWebSocketListener(listenerId);
      };
    };

    listenWebsocket();
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const jobsStatusCount = excelJobs.data?.reduce(
    (acc, job) => {
      if (job.status === 'processing') acc.processing++;
      if (job.status === 'failed') acc.failed++;
      if (job.status === 'completed') acc.completed++;
      return acc;
    },
    { processing: 0, failed: 0, completed: 0 },
  );

  if ((excelJobs.data?.length || 0) > 0) {
    return (
      <>
        <ButtonStyled
          data-testid='excel-download-progress-button'
          $error={jobsStatusCount!.processing === 0 && jobsStatusCount!.failed > 0}
          onClick={handleClick}
        >
          <Stack sx={{ height: '100%' }} gap={1} alignItems='center'>
            <Stack flexDirection='row' alignItems='center' justifyContent='center' flex={1} gap={1}>
              <img src='/images/excel.svg' alt='excel' />
              {jobsStatusCount!.processing > 0 ? (
                <Typography color={SdxColors.white} variant='subtitle1'>
                  {excelJobs.data!.length > 1 ? excelJobs.data!.length : `${excelJobs.data![0].progress}%`}
                </Typography>
              ) : jobsStatusCount!.failed > 0 ? (
                <Warning1 style={{ ...filterSdxIconToRedLight, width: 20, height: 20 }} />
              ) : (
                <CheckCicle style={{ ...filterSdxIconToWhite, width: 20, height: 20 }} />
              )}
            </Stack>
            {jobsStatusCount!.processing > 0 && (
              <BarLoader height={1} color={SdxColors.white} cssOverride={{ width: '100%' }} />
            )}
          </Stack>
        </ButtonStyled>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Stack flexDirection='column' p={1}>
            {excelJobs.data?.map(job => (
              <Stack key={job.id} flexDirection='row' alignItems='center' gap={2}>
                <Typography variant='subtitle2'>
                  {new Intl.DateTimeFormat(i18n.language, {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  }).format(job.createdAt)}
                </Typography>

                {/*Loader*/}
                {job.status === 'processing' && (
                  <LinearProgress variant='determinate' value={job.progress} style={{ width: 150 }} />
                )}
                {(job.status === 'failed' || job.status === 'completed') && <Box width={150} />}

                {/*Action*/}
                {job.status === 'processing' && (
                  <IconButton onClick={() => abortExcel.mutate({ jobId: job.id })}>
                    <Close style={{ width: 20, height: 20 }} />
                  </IconButton>
                )}
                {job.status === 'failed' && (
                  <IconButton>
                    <Warning style={{ width: 20, height: 20, ...filterSdxIconToRedDark }} />
                  </IconButton>
                )}
                {job.status === 'completed' && (
                  <IconButton
                    disabled={downloadExcel.isPending}
                    onClick={() => downloadExcel.mutate({ jobId: job.id })}
                  >
                    {downloadExcel.isPending ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Download style={{ width: 20, height: 20 }} />
                    )}
                  </IconButton>
                )}
              </Stack>
            ))}
          </Stack>
        </Popover>
      </>
    );
  }

  return null;
}

function onWebsocketMessage(event: MessageEvent, downloadExcel: ReturnType<typeof useDownloadExcel>) {
  const data = JSON.parse(event.data) as { event: string; data: ExcelJobDTO };
  if (data.event !== 'excelProgress') return;
  updateExcelJobInCache(data.data);

  if (data.data.status === 'failed') showErrorToast(i18n.t('components.excelDownloadProgress.errorToast'));
  if (data.data.status === 'completed') {
    downloadExcel.mutate({ jobId: data.data.id });
    showSuccessToast(i18n.t('components.excelDownloadProgress.successToast'));
  }
}

const ButtonStyled = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== '$error',
})<{ $error: boolean }>(({ theme, $error }) => ({
  height: '100%',
  border: `1px solid ${$error ? SdxColors.redLight : SdxColors.blueLighter}`,
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
}));
